import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ChevronUp, ChevronDown, Trash2 } from 'lucide-react';
import apiService from '../../services/api';
import {
  RegistryStatsResponse,
  RegistryEntryResponse,
  Model
} from '../../types';
import RegistryEntryDetail from './RegistryEntryDetail';
import CustomSelect from '../common/CustomSelect';

type SortColumn = 'created_at' | 'expires_at' | 'source_agent_id' | 'dedup_key';
type SortDirection = 'asc' | 'desc';

interface RegistryEntriesListProps {
  model: Model;
  onEntriesChanged?: () => void;
}

/**
 * RegistryEntriesList - Displays a list of registry entries for a model
 *
 * Features:
 * - Statistics cards (total entries, expiring soon, added this week, by agent)
 * - Table with pagination, search, and filtering
 * - Entry detail modal on row click
 * - Loading, error, and empty states
 */
const RegistryEntriesList: React.FC<RegistryEntriesListProps> = ({ model, onEntriesChanged }) => {
  // State for data
  const [stats, setStats] = useState<RegistryStatsResponse | null>(null);
  const [entries, setEntries] = useState<RegistryEntryResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 10;

  // Filter state
  const [searchText, setSearchText] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);
  const [createdAfter, setCreatedAfter] = useState('');
  const [createdBefore, setCreatedBefore] = useState('');
  const [expiresWithinDays, setExpiresWithinDays] = useState<number | null>(null);
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);

  // Debounced search for API calls
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Detail modal state
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);

  // Delete all state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Sort state - default to created_at descending (newest first)
  const [sortBy, setSortBy] = useState<SortColumn>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Track model updates for refresh - include in key dependency
  const modelUpdatedAt = model.updated_at;

  const modelId = model.model_id;

  // Debounce search input - only when typing, not when clearing
  useEffect(() => {
    // If search was cleared (empty string), update immediately
    if (searchText === '' && debouncedSearch !== '') {
      setDebouncedSearch('');
      return;
    }

    // Otherwise, debounce for 300ms
    const timer = setTimeout(() => {
      if (searchText !== debouncedSearch) {
        setDebouncedSearch(searchText);
        // Reset to page 1 when search changes
        setCurrentPage(1);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchText, debouncedSearch]);

  // Load data function
  const loadData = useCallback(async () => {
    // Skip loading if there's a date range validation error
    if (dateRangeError) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Build filter parameters
      const params: any = {
        page: currentPage,
        page_size: pageSize,
        search: debouncedSearch || undefined,
        source_agent_id: selectedAgentId || undefined,
        sort_by: sortBy,
        sort_direction: sortDirection,
      };

      // Add created_after filter (convert date to ISO format)
      if (createdAfter) {
        params.created_after = new Date(createdAfter).toISOString();
      }

      // Add created_before filter (convert date to ISO format, set to end of day)
      if (createdBefore) {
        const endOfDay = new Date(createdBefore);
        endOfDay.setHours(23, 59, 59, 999);
        params.created_before = endOfDay.toISOString();
      }

      // Add expires_within_days filter
      if (expiresWithinDays !== null) {
        params.expires_within_days = expiresWithinDays;
      }

      // Load stats and entries in parallel
      const [statsData, entriesData] = await Promise.all([
        apiService.getRegistryStats(modelId),
        apiService.getRegistryEntries(modelId, params)
      ]);

      setStats(statsData);
      setEntries(entriesData.entries);
      setTotalPages(entriesData.total_pages);
      setTotal(entriesData.total);
    } catch (err) {
      setError('Error loading registry data');
    } finally {
      setLoading(false);
    }
  // Include modelUpdatedAt to trigger reload when model configuration changes
  }, [modelId, currentPage, debouncedSearch, selectedAgentId, createdAfter, createdBefore, expiresWithinDays, dateRangeError, modelUpdatedAt, sortBy, sortDirection]);

  // Load data on mount and when dependencies change
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Format date for display
  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return '1 day ago';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      // Return formatted date like "Dec 10, 2025"
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  };

  // Get a displayable dedup key value
  const getDedupKeyDisplay = (entry: RegistryEntryResponse): string => {
    if (!entry.dedup_key_value) return entry.dedup_key_hash;

    // Try to extract meaningful value from dedup_key_value
    const values = Object.values(entry.dedup_key_value);
    if (values.length > 0) {
      // Return first non-null string value or JSON representation
      for (const val of values) {
        if (typeof val === 'string' && val.trim()) {
          return val;
        }
      }
      return JSON.stringify(entry.dedup_key_value);
    }
    return entry.dedup_key_hash;
  };

  // Get agent filter options from stats
  const agentOptions = useMemo(() => {
    return stats?.entries_by_agent || [];
  }, [stats]);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  // Handle agent filter change
  const handleAgentFilterChange = (value: string) => {
    setSelectedAgentId(value ? parseInt(value, 10) : null);
    setCurrentPage(1); // Reset to page 1 when filter changes
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setSearchText('');
    setSelectedAgentId(null);
    setCreatedAfter('');
    setCreatedBefore('');
    setExpiresWithinDays(null);
    setDateRangeError(null);
    setCurrentPage(1);
  };

  // Handle created after date change
  const handleCreatedAfterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCreatedAfter(value);
    setCurrentPage(1); // Reset to page 1 when filter changes

    // Validate date range
    if (value && createdBefore && new Date(value) > new Date(createdBefore)) {
      setDateRangeError('Created after date must be before created before date');
    } else {
      setDateRangeError(null);
    }
  };

  // Handle created before date change
  const handleCreatedBeforeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCreatedBefore(value);
    setCurrentPage(1); // Reset to page 1 when filter changes

    // Validate date range
    if (createdAfter && value && new Date(createdAfter) > new Date(value)) {
      setDateRangeError('Created after date must be before created before date');
    } else {
      setDateRangeError(null);
    }
  };

  // Handle expiring soon filter change
  const handleExpiresWithinDaysChange = (value: string) => {
    setExpiresWithinDays(value ? parseInt(value, 10) : null);
    setCurrentPage(1); // Reset to page 1 when filter changes
  };

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchText) count++;
    if (selectedAgentId !== null) count++;
    if (createdAfter) count++;
    if (createdBefore) count++;
    if (expiresWithinDays !== null) count++;
    return count;
  }, [searchText, selectedAgentId, createdAfter, createdBefore, expiresWithinDays]);

  // Handle refresh
  const handleRefresh = () => {
    loadData();
  };

  // Handle page navigation
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Handle column header click for sorting
  const handleSort = (column: SortColumn) => {
    if (sortBy === column) {
      // Toggle direction if clicking same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to descending
      setSortBy(column);
      setSortDirection('desc');
    }
    setCurrentPage(1); // Reset to page 1 when sort changes
  };

  // Handle row click to open detail modal
  const handleRowClick = (entryId: string) => {
    setSelectedEntryId(entryId);
  };

  // Handle modal close
  const handleCloseDetail = () => {
    setSelectedEntryId(null);
  };

  // Handle delete all entries
  const handleDeleteAll = async () => {
    try {
      setDeleting(true);
      await apiService.deleteAllRegistryEntries(modelId);
      setShowDeleteConfirm(false);
      setCurrentPage(1);
      loadData();
      onEntriesChanged?.();
    } catch (err) {
      setError('Error deleting registry entries');
    } finally {
      setDeleting(false);
    }
  };

  // Render stats cards
  const renderStatsCards = () => (
    <div className="grid grid-cols-4 gap-4 mb-6">
      <div className="bg-white dark:bg-cap-dark p-4 rounded shadow">
        <div className="text-2xl font-bold">{stats?.total_entries ?? 0}</div>
        <div className="text-gray-500 dark:text-gray-400">Total Entries</div>
      </div>
      <div className="bg-white dark:bg-cap-dark p-4 rounded shadow">
        <div className="text-2xl font-bold">{stats?.expiring_soon ?? 0}</div>
        <div className="text-gray-500 dark:text-gray-400">Expiring Soon</div>
      </div>
      <div className="bg-white dark:bg-cap-dark p-4 rounded shadow">
        <div className="text-2xl font-bold">
          +{stats?.added_this_week ?? 0}
        </div>
        <div className="text-gray-500 dark:text-gray-400">Added This Week</div>
      </div>
      <div className="bg-white dark:bg-cap-dark p-4 rounded shadow">
        <div className="text-sm">
          {stats?.entries_by_agent?.map(agent => (
            <div key={agent.agent_id} data-testid={`agent-stat-${agent.agent_id}`}>
              {agent.agent_name}: {agent.count} entries
            </div>
          ))}
          {(!stats?.entries_by_agent || stats.entries_by_agent.length === 0) && (
            <div className="text-gray-400">No agents</div>
          )}
        </div>
        <div className="text-gray-500 dark:text-gray-400">By Agent</div>
      </div>
    </div>
  );

  // Render filter controls
  const renderFilters = () => (
    <div className="mb-4">
      <div className="flex flex-wrap items-center gap-4 mb-2">
        {/* Search input */}
        <input
          type="text"
          placeholder="Search entries..."
          value={searchText}
          onChange={handleSearchChange}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        />

        {/* Agent filter */}
        <CustomSelect
          aria-label="Filter by agent"
          value={String(selectedAgentId || '')}
          onChange={handleAgentFilterChange}
          options={[
            { value: '', label: 'All Agents' },
            ...agentOptions.map(agent => ({
              value: String(agent.agent_id),
              label: agent.agent_name,
            })),
          ]}
        />

        {/* Created after date filter */}
        <div className="flex items-center gap-1">
          <label htmlFor="created-after" className="text-sm text-gray-600 dark:text-gray-400">
            Created after
          </label>
          <input
            id="created-after"
            type="date"
            aria-label="Created after"
            value={createdAfter}
            onChange={handleCreatedAfterChange}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/* Created before date filter */}
        <div className="flex items-center gap-1">
          <label htmlFor="created-before" className="text-sm text-gray-600 dark:text-gray-400">
            Created before
          </label>
          <input
            id="created-before"
            type="date"
            aria-label="Created before"
            value={createdBefore}
            onChange={handleCreatedBeforeChange}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/* Expiring soon filter */}
        <div className="flex items-center gap-1">
          <label htmlFor="expiring-soon" className="text-sm text-gray-600 dark:text-gray-400">
            Expiring soon
          </label>
          <CustomSelect
            aria-label="Expiring soon"
            value={String(expiresWithinDays ?? '')}
            onChange={handleExpiresWithinDaysChange}
            options={[
              { value: '', label: 'All entries' },
              { value: '7', label: 'Within 7 days' },
              { value: '30', label: 'Within 30 days' },
              { value: '60', label: 'Within 60 days' },
              { value: '90', label: 'Within 90 days' },
            ]}
          />
        </div>

        {/* Clear filters button */}
        <button
          onClick={handleClearFilters}
          className="px-3 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900"
        >
          Clear Filters
        </button>

        {/* Refresh button */}
        <button
          onClick={handleRefresh}
          className="px-3 py-2 text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 border border-primary-300 dark:border-primary-700 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900/30"
        >
          Refresh
        </button>

        {/* Delete All button */}
        {total > 0 && (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="px-3 py-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 border border-red-300 dark:border-red-700 rounded-md hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center gap-1"
          >
            <Trash2 className="w-4 h-4" />
            Delete All
          </button>
        )}

        {/* Active filter count indicator */}
        {activeFilterCount > 0 && (
          <span
            data-testid="active-filters-count"
            className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded-full text-sm font-medium"
          >
            {activeFilterCount}
          </span>
        )}
      </div>

      {/* Date range validation error */}
      {dateRangeError && (
        <div className="text-red-600 text-sm mt-1">{dateRangeError}</div>
      )}
    </div>
  );

  // Render sort indicator
  const renderSortIndicator = (column: SortColumn) => {
    if (sortBy !== column) {
      return <span className="ml-1 text-gray-300 opacity-0 group-hover:opacity-100"><ChevronDown className="w-4 h-4 inline" /></span>;
    }
    return sortDirection === 'asc'
      ? <ChevronUp className="w-4 h-4 inline ml-1 text-primary-600" />
      : <ChevronDown className="w-4 h-4 inline ml-1 text-primary-600" />;
  };

  // Render table
  const renderTable = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-900">
          <tr>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 group select-none"
              onClick={() => handleSort('dedup_key')}
            >
              Dedup Key
              {renderSortIndicator('dedup_key')}
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 group select-none"
              onClick={() => handleSort('source_agent_id')}
            >
              Source Agent
              {renderSortIndicator('source_agent_id')}
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 group select-none"
              onClick={() => handleSort('created_at')}
            >
              Created
              {renderSortIndicator('created_at')}
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 group select-none"
              onClick={() => handleSort('expires_at')}
            >
              Expires
              {renderSortIndicator('expires_at')}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-cap-dark divide-y divide-gray-200 dark:divide-gray-700">
          {entries.map(entry => (
            <tr
              key={entry.id}
              onClick={() => handleRowClick(entry.id)}
              className="hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 cursor-pointer"
            >
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-warm-white">
                {getDedupKeyDisplay(entry)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                {entry.source_agent_name || 'N/A'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                {formatDate(entry.created_at)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                {formatDate(entry.expires_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Render pagination
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Page {currentPage} of {totalPages}
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handlePreviousPage}
            disabled={currentPage <= 1}
            className={`px-3 py-1 rounded ${
              currentPage <= 1
                ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            Previous
          </button>
          <button
            onClick={handleNextPage}
            disabled={currentPage >= totalPages}
            className={`px-3 py-1 rounded ${
              currentPage >= totalPages
                ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // Error state (only if not loading and we have an error)
  if (error && !loading) {
    return (
      <div className="p-4">
        {/* Always render filters even in error state */}
        {renderFilters()}
        <div className="text-red-600" role="alert">
          Error loading registry data
        </div>
      </div>
    );
  }

  // Render content - always show filters, stats and table
  const renderContent = () => {
    // Initial loading state - show filters with loading indicator
    if (loading && !stats && entries.length === 0) {
      return (
        <div className="animate-pulse text-gray-500 dark:text-gray-400">Loading entries...</div>
      );
    }

    if (entries.length === 0 && !loading) {
      // Empty state
      return (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No entries found
        </div>
      );
    }

    // Normal state with entries (may be loading more data)
    return (
      <>
        {loading && <div className="animate-pulse text-sm text-gray-500 dark:text-gray-400 mb-2">Refreshing...</div>}
        {renderTable()}
        {renderPagination()}
      </>
    );
  };

  return (
    <div className="p-4">
      {renderStatsCards()}
      {renderFilters()}
      {renderContent()}

      {/* Entry Detail Modal */}
      <RegistryEntryDetail
        modelId={modelId}
        entryId={selectedEntryId}
        onClose={handleCloseDetail}
      />

      {/* Delete All Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-2">
              Delete All Registry Entries
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              This will permanently delete all {total} {total === 1 ? 'entry' : 'entries'} from this registry. Agents will treat previously seen items as new on their next run. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAll}
                disabled={deleting}
                className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed"
              >
                {deleting ? 'Deleting...' : 'Delete All'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistryEntriesList;
